/** Contratos de autenticação (SPEC-IDN-001, 002, 003).
 *
 * O refresh token não aparece aqui de propósito: ele vive em cookie
 * `HttpOnly` e o JavaScript não o enxerga. Quem o envia é o navegador.
 */

import { http } from "@/lib/http";
import type { components } from "@/lib/api-types";

export type Usuario = components["schemas"]["UserPayload"];

export type Tenant = components["schemas"]["TenantPayload"];

export type RespostaDeSessao = components["schemas"]["AuthResponse"];

export type DadosDeCadastro = components["schemas"]["SignUpRequest"];

export async function cadastrar(dados: DadosDeCadastro): Promise<RespostaDeSessao> {
  return http.post<RespostaDeSessao>("/auth/signup", dados);
}

/** A senha estava certa e falta o segundo fator (SPEC-IDN-002 § 3).
 *
 * Forma inteiramente diferente de `RespostaDeSessao`, e não a mesma com campos
 * vazios: **não existe token de sessão aqui para um cliente distraído guardar.**
 */
export type RespostaDeSegundoFator = components["schemas"]["MfaRequiredResponse"];

export type RespostaDeEntrada = RespostaDeSessao | RespostaDeSegundoFator;

/** Discriminador do union. Função e não `"mfa_required" in x` espalhado pela
 * tela: o nome diz o que a checagem significa. */
export function pedeSegundoFator(resposta: RespostaDeEntrada): resposta is RespostaDeSegundoFator {
  return "mfa_required" in resposta && resposta.mfa_required;
}

export async function entrar(email: string, senha: string): Promise<RespostaDeEntrada> {
  return http.post<RespostaDeEntrada>("/auth/login", { email, password: senha });
}

/** Conclui o login com o código do autenticador ou de recuperação. */
export async function concluirSegundoFator(
  mfaToken: string,
  codigo: string,
): Promise<RespostaDeSessao> {
  return http.post<RespostaDeSessao>("/auth/mfa/verify", {
    mfa_token: mfaToken,
    codigo,
  });
}

/** Renovação em voo, compartilhada por todos os chamadores.
 *
 * Duas renovações simultâneas **derrubam a sessão inteira**, e não é exagero de
 * defesa: o refresh é rotativo e o servidor trata token já rotacionado como
 * reuso (SPEC-IDN-003) — a resposta é revogar a família toda. Quem chamou por
 * último some junto com quem chamou primeiro.
 *
 * E a chamada dupla acontece de verdade: o `StrictMode` invoca o efeito de
 * montagem duas vezes em desenvolvimento, então **todo F5 dispara dois
 * refresh** com o mesmo cookie. Quando o primeiro rotaciona antes de o segundo
 * ser processado, o segundo é reuso e a pessoa cai no login sem ter feito nada.
 * Em produção o efeito roda uma vez, mas duas abas abertas juntas reproduzem a
 * mesma corrida.
 *
 * O cliente HTTP já tinha esta proteção para a renovação disparada por 401
 * (`#renovacaoEmVoo`). Faltava aqui, que é o caminho da montagem.
 */
let renovacaoEmVoo: Promise<RespostaDeSessao> | null = null;

export async function renovar(): Promise<RespostaDeSessao> {
  renovacaoEmVoo ??= http.post<RespostaDeSessao>("/auth/refresh").finally(() => {
    renovacaoEmVoo = null;
  });
  return renovacaoEmVoo;
}

export async function sair(): Promise<void> {
  // 204 sem corpo: o cliente devolve `undefined`, e é isso que se descarta.
  await http.post<undefined>("/auth/logout");
}

/** Confirma quem o token diz que você é. Não toca o banco.
 *
 * A resposta real é `SessaoAtualResponse` — divergente de `Usuario`
 * (`UserPayload`, o formato de login/cadastro): esta rota só lê o token, e o
 * token não carrega `email`/`email_verified`/`mfa_enabled` nem garante
 * `full_name` não nulo. O tipo antigo (`{ user: Usuario; tenant: { id } }`)
 * prometia campos que a rota nunca devolveu; ver relatório da migração. */
export async function eu(): Promise<components["schemas"]["SessaoAtualResponse"]> {
  return http.get("/auth/me");
}
